export interface ViewerAssetSetLike {
    ID: string;
    Type: number;
    VersionToken?: string;
    KnowledgeBookID?: string | null;
}

const UNASSIGNED_ASSET_LAYER_ID = '__asset_set_unassigned__';

export const resolveFallbackAssetLayerID = (
    allAssetSetID: string | null | undefined,
    hasOrdinaryAssetSetRoots: boolean,
    assetSetIsolation: boolean
): string | null => {
    if (!allAssetSetID && !hasOrdinaryAssetSetRoots && !assetSetIsolation) return null;
    return allAssetSetID ?? UNASSIGNED_ASSET_LAYER_ID;
};

export const shouldUseKnowledgeBookAssetSetManageMode = <T extends ViewerAssetSetLike>(
    openedKnowledgeBookID: string | null | undefined,
    knowledgeBookViewingMode: string,
    selectedAssetSet: T | null,
    knowledgeBookAssetSets: T[]
): boolean => !!openedKnowledgeBookID
    && knowledgeBookViewingMode === 'manageAssets'
    && (!selectedAssetSet?.ID
        || !!selectedAssetSet.KnowledgeBookID
        || knowledgeBookAssetSets.some(assetSet => assetSet.ID === selectedAssetSet.ID));

export const canModifyAssetSetMembership = <T extends ViewerAssetSetLike>(
    assetSetManageMode: boolean,
    selectedAssetSet: T | null,
    listAssetSetType: number
): boolean => assetSetManageMode && selectedAssetSet?.Type === listAssetSetType;

interface ResolveViewerAssetSetsOptions<T extends ViewerAssetSetLike> {
    knowledgeViewAssetSetIDs: string[] | null;
    knowledgeBookAssetSets: T[];
    facilityAssetSets: T[];
    selectedAssetSet: T | null;
    knowledgeBookManageMode: boolean;
    facilityMode: 'manage' | 'display' | null;
    displayAssetSetIDs?: string[];
}

const normalizeAssetSetID = (id: string): string => id.toLocaleLowerCase();

export const mergeViewerAssetSets = <T extends ViewerAssetSetLike>(
    knowledgeBookAssetSets: readonly T[],
    facilityAssetSets: readonly T[]
): T[] => {
    const merged: T[] = [];
    const seen = new Set<string>();

    [...knowledgeBookAssetSets, ...facilityAssetSets].forEach(assetSet => {
        const key = normalizeAssetSetID(assetSet.ID);
        if (!key || seen.has(key)) return;
        seen.add(key);
        merged.push(assetSet);
    });

    return merged;
};

export const areViewerAssetSetsResolved = <T extends ViewerAssetSetLike>(
    assetSets: readonly T[],
    ids: readonly string[]
): boolean => {
    const available = new Set(assetSets.map(assetSet => normalizeAssetSetID(assetSet.ID)));
    return ids.every(id => available.has(normalizeAssetSetID(id)));
};

const selectByIDs = <T extends ViewerAssetSetLike>(
    assetSets: readonly T[],
    ids: readonly string[]
): T[] => {
    const byID = new Map(assetSets.map(assetSet => [normalizeAssetSetID(assetSet.ID), assetSet]));
    const selected = new Set<string>();
    return ids.flatMap(id => {
        const key = normalizeAssetSetID(id);
        if (selected.has(key)) return [];
        const assetSet = byID.get(key);
        if (!assetSet) return [];
        selected.add(key);
        return [assetSet];
    });
};

export const resolveViewerAssetSets = <T extends ViewerAssetSetLike>({
    knowledgeViewAssetSetIDs,
    knowledgeBookAssetSets,
    facilityAssetSets,
    selectedAssetSet,
    knowledgeBookManageMode,
    facilityMode,
    displayAssetSetIDs = [],
}: ResolveViewerAssetSetsOptions<T>): T[] => {
    if (knowledgeViewAssetSetIDs !== null) {
        return selectByIDs(
            mergeViewerAssetSets(knowledgeBookAssetSets, facilityAssetSets),
            knowledgeViewAssetSetIDs
        );
    }

    if (knowledgeBookManageMode) {
        return selectedAssetSet?.ID && selectedAssetSet.KnowledgeBookID
            ? [selectedAssetSet]
            : [];
    }

    if (facilityMode === 'manage') {
        return selectedAssetSet?.ID && !selectedAssetSet.KnowledgeBookID
            ? [selectedAssetSet]
            : [];
    }

    if (facilityMode === 'display') {
        // Sparse Display Priority is the only source of population demand.
        return selectByIDs(
            facilityAssetSets.filter(assetSet => !assetSet.KnowledgeBookID),
            displayAssetSetIDs
        );
    }

    return [];
};

export const getAssetSetDefinitionSignature = (
    assetSets: ViewerAssetSetLike[]
): string => assetSets
    .map(assetSet => `${assetSet.ID}|${assetSet.Type}|${assetSet.VersionToken ?? ''}`)
    .sort()
    .join('||');

export const getAssetSetPopulationSignature = (assetSet: ViewerAssetSetLike): string =>
    `${assetSet.Type}|${assetSet.VersionToken ?? ''}`;

export const planRetainedAssetSetRootUpdate = <T extends ViewerAssetSetLike>(
    assetSets: T[],
    registeredRootVersions: ReadonlyMap<string, string>,
    currentFallbackAssetLayerID: string | null,
    assetSetIsolation: boolean
) => {
    const ordinarySets = (assetSets || []).filter(assetSet =>
        assetSet.Type !== AssetSetType.All3DAssets && assetSet.Type !== AssetSetType.AllAssetTags);
    const allAssetSet = (assetSets || []).find(assetSet => assetSet.Type === AssetSetType.All3DAssets);
    const fallbackAssetLayerID = resolveFallbackAssetLayerID(
        allAssetSet?.ID,
        ordinarySets.length > 0,
        assetSetIsolation
    );
    const hasRegisteredVersionChange = ordinarySets.some(assetSet => {
        const registeredVersion = registeredRootVersions.get(assetSet.ID.toLocaleLowerCase());
        return registeredVersion !== undefined && registeredVersion !== (assetSet.VersionToken ?? '');
    });
    const canAdoptFallbackInPlace =
        currentFallbackAssetLayerID == null &&
        fallbackAssetLayerID != null &&
        !!allAssetSet &&
        ordinarySets.length === 0 &&
        registeredRootVersions.size === 0 &&
        !assetSetIsolation;

    return {
        ordinarySets,
        fallbackAssetLayerID,
        canAdoptFallbackInPlace,
        requiresFullRebuild: hasRegisteredVersionChange ||
            (fallbackAssetLayerID !== currentFallbackAssetLayerID && !canAdoptFallbackInPlace),
        missingSets: ordinarySets.filter(assetSet =>
            !registeredRootVersions.has(assetSet.ID.toLocaleLowerCase())),
    };
};

export const planAssetSetMembershipDelta = <T extends ViewerAssetSetLike>(
    assetSets: T[],
    activeMemberships: ReadonlyMap<string, { id: string; signature: string }>
) => {
    const desired = new Map((assetSets || []).map(assetSet => [
        assetSet.ID.trim().toLocaleLowerCase(),
        assetSet
    ]));

    return {
        deactivatedSetIds: Array.from(activeMemberships.entries())
            .filter(([key]) => !desired.has(key))
            .map(([, active]) => active.id),
        setsToActivate: (assetSets || []).filter(assetSet => {
            const active = activeMemberships.get(assetSet.ID.trim().toLocaleLowerCase());
            return !active || active.signature !== getAssetSetPopulationSignature(assetSet);
        }),
    };
};

export const getViewerGeometryContextSignature = (
    ontologyID?: string | null,
    facilityID?: string | null,
    rangePosition?: { X: number; Y: number; Z: number } | null,
    distanceThreshold?: number | null
): string => [
    facilityID ?? '',
    ontologyID ?? '',
    rangePosition
        ? `${rangePosition.X},${rangePosition.Y},${rangePosition.Z}`
        : '',
    distanceThreshold ?? 0,
].join('|');
